import React, { useEffect, useMemo } from 'react';
import './AccordanceTrello.scss';
import Board from 'react-trello';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AccordanceCard from '../AccordanceCard/AccordanceCard';
import AccordanceCancel from '../../../../Util/Modals/AccordanceCancel/AccordanceCancel';
import AccordanceFulfilled from '../../../../Util/Modals/AccordanceFulfilled/AccordanceFulfilled';
import { CANCELLED_ACCORDANCES_LANE_KEY, newDataAfterDrag, PRESET_ACCORDANCE_CARDS, translateLaneTitle } from './util';
import { isMobile } from '../../../../../services/booleanUtil';
import CollapsedEements from './CollapsedElements/CollapsedElements';
import CustomHeader from './CustomHeader/CustomHeader';
import Project from '../../../../../models/Project';
import { useEventContext } from '../../../../../context/EventContext';
import { SkeletonCard } from './SkeletonCard';

export enum modalType {
  Cancel,
  FulFilled,
  NewAccordance,
}

type Props = {
  data: any;
  isLoading?: boolean;
  updateData: Function;
  boardStyle: any;
};

const TrelloBoard = (props: Props) => {
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [showFulfilledModal, setShowFulfilledModal] = React.useState(false);
  const [dragEndData, setDragEndData] = React.useState<any>(null);
  const [eventBus, setEventBus] = React.useState<any>(undefined);
  const currentProject = useSelector<any, Project>(state => state.currentProject);

  const { data, updateData, isLoading, boardStyle } = props;
  const { getDataEvent, setEventId, eventId, getDataValidity } = useEventContext();

  const { t } = useTranslation();

  useEffect(() => {
    data.lanes.map(lane => {
      return translateLaneTitle(lane, t);
    });
  }, [data, t]);

  useEffect(() => {
    if (eventId && currentProject) {
      getLogsFromAccordance();
    }
  }, [eventId]);

  const getLogsFromAccordance = () => {
    getDataEvent();
    getDataValidity(currentProject.id);
  }

  const getDefaultLaneData = (data) => {
    const clonedData = {
      ...data,
      lanes: data.lanes.map(lane => {
        const laneTranslated = { ...translateLaneTitle({ ...lane }, t) };

        if (isLoading) {
          laneTranslated.cards = PRESET_ACCORDANCE_CARDS;
        }

        if (laneTranslated.key === CANCELLED_ACCORDANCES_LANE_KEY) {
          laneTranslated.style = isLoading ? null : data.lanes.find(lane => lane.key === CANCELLED_ACCORDANCES_LANE_KEY)?.style;
        }

        return laneTranslated;
      }),
    };

    return clonedData;
  };

  const updateDataCloseModal = (undoChanges: boolean) => {
    if (undoChanges) {
      const newLaneData = [...data.lanes];
      if (eventBus) {
        eventBus.publish({ type: 'UPDATE_LANES', lanes: newLaneData });
      }
    } else {
      updateDataAfterDragEnd();
    }
    setShowCancelModal(false);
    setShowFulfilledModal(false);
  };

  const updateDataAfterDragEnd = () => {
    const { cardId, sourceLaneId, targetLaneId, cardDetails } = dragEndData;
    const updatedCard = {
      ...cardDetails,
      accordance: { ...cardDetails.accordance, accordanceState: parseInt(targetLaneId) },
    };
    const card = { ...updatedCard, draggable: false };
    if (hasDataToEdit(sourceLaneId, targetLaneId, cardDetails)) {
      const newData = newDataAfterDrag(data, { ...dragEndData, cardDetails: updatedCard });
      updateData(newData);
      setTimeout(() => {
        eventBus.publish({ type: 'ADD_CARD', laneId: targetLaneId, card });
        eventBus.publish({ type: 'REMOVE_CARD', laneId: sourceLaneId, cardId });
      }, 0);
      setDragEndData(null);
    }
  };

  const handleDragEnd = (
    cardId: string,
    sourceLaneId: string,
    targetLaneId: string,
    position: number,
    cardDetails: any,
  ) => {
    setDragEndData({ cardId, sourceLaneId, targetLaneId, position, cardDetails });
    displayConfirmModal(targetLaneId);
  };

  const displayConfirmModal = (targetLaneId: string) => {
    if (targetLaneId === '4') {
      setShowCancelModal(true);
    } else if (targetLaneId === '3') {
      setShowFulfilledModal(true);
    }
  };

  const handleCardClick = (cardId, metadata, laneId) => {
    if (eventId === cardId) {
      getLogsFromAccordance();
    } else {
      setEventId(cardId);
    }
  };

  const boardData = useMemo(
    () => (isLoading ? getDefaultLaneData(data) : data),
    [isLoading, data, t]
  );

  return (
    <>
      <Board
        className={isLoading ? 'board-loading' : ''}
        handleDragEnd={(cardId, sourceLaneId, targetLaneId, position, cardDetails) => {
          handleDragEnd(cardId, sourceLaneId, targetLaneId, position, cardDetails);
        }}
        data={boardData}
        style={boardStyle || {}}
        components={{
          Card: isLoading ? SkeletonCard : AccordanceCard,
          LaneHeader: CustomHeader,
        }}
        laneDraggable={false}
        eventBusHandle={setEventBus}
        cardDraggable={false}
        onCardClick={handleCardClick}
      />
      {!isMobile() && <CollapsedEements data={boardData} />}
      <AccordanceCancel
        isOpen={showCancelModal}
        closeModal={(undoChanges: boolean) => updateDataCloseModal(undoChanges)}
        accordanceId={getAccordanceId(dragEndData)}
      />
      <AccordanceFulfilled
        isOpen={showFulfilledModal}
        closeModal={(undoChanges: boolean) => updateDataCloseModal(undoChanges)}
        accordanceId={getAccordanceId(dragEndData)}
      />
    </>
  );
};

export default TrelloBoard;

const getAccordanceId = dragEndData => (dragEndData ? dragEndData.cardId : 0);
const hasDataToEdit = (sourceLaneId, targetLaneId, cardDetails) => sourceLaneId && targetLaneId && cardDetails;
